/* eslint-disable */
import React, { useState, useMemo } from 'react';
import {
	Table,
	Typography,
	Button,
	Tooltip,
	Space,
	Tag,
	Card,
	Statistic,
	Row,
	Col,
	Modal,
	List,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	InfoCircleOutlined,
	TrophyOutlined,
	CrownOutlined,
	RocketOutlined,
	// ClockCircleOutlined,
	MessageOutlined,
	ThunderboltOutlined,
	GithubOutlined,
} from '@ant-design/icons';
import { PRDetails, PRStatsWithKey } from '../types';
import { ScoreExplanationModal } from './ScoreExplanationModal';
import { SCORE_EXPLANATIONS } from '../types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const { Text, Title, Paragraph } = Typography;

interface EnhancedPRStatsTableProps {
	data: PRStatsWithKey[];
	loading?: boolean;
	dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

interface MetricModalProps {
	visible: boolean;
	onClose: () => void;
	title: string;
	author: string;
	metric: string;
	value: number;
	rank: number;
	total: number;
	data: PRStatsWithKey[];
	dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const MetricModal: React.FC<MetricModalProps> = ({
	visible,
	onClose,
	title,
	author,
	metric,
	value,
	rank,
	total,
	data,
	dateRange,
}) => {
	const percentile = Math.round(((total - rank + 1) / total) * 100);
	const details = data.find((r) => r.author === author)?.details;
	console.log('details', details);
	console.log('data', data);

	// Determine which section to show based on the metric
	const showSection = {
		comments: metric.toLowerCase() === 'comments on others prs',
		reviews: metric.toLowerCase() === 'pr reviews',
		prs: metric.toLowerCase().includes('prs'),
	};

	// Filter PRs based on date range if it exists
	const filterPRsByDateRange = (prs: PRDetails[] = []) => {
		if (!dateRange) {
			return prs;
		}
		const startDate = new Date(dateRange[0].toISOString());
		const endDate = new Date(dateRange[1].toISOString());
		return prs.filter((pr) => {
			const prDate = new Date(pr.createdAt);
			return prDate >= startDate && prDate <= endDate;
		});
	};

	// Separate PRs into open and closed
	const allPRs = details?.prs || [];
	const filteredPRs = filterPRsByDateRange(allPRs);
	const openPRs = filteredPRs.filter((pr) => pr.state === 'open');
	const closedPRs = filteredPRs.filter((pr) => pr.state === 'closed');

	return (
		<Modal title={title} open={visible} onCancel={onClose} footer={null} width={800}>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Card>
					<Statistic
						title="Current Value"
						value={value}
						precision={1}
						suffix={metric === 'avgDaysToClose' ? ' days' : ''}
					/>
					<Paragraph style={{ marginTop: 16 }}>
						Ranked {rank} out of {total} ({percentile}th percentile)
					</Paragraph>
				</Card>

				{showSection.comments && details?.comments && (
					<Card title="All Comments">
						<List
							size="small"
							dataSource={details.comments}
							renderItem={(item: PRDetails) => (
								<List.Item>
									<Space
										direction="vertical"
										size={2}
										style={{ width: '100%', wordBreak: 'break-word' }}
									>
										<Text>{item.body || item.title}</Text>
										<Space>
											<Tag
												color={
													item.type === 'issue_comment'
														? 'blue'
														: item.type === 'review_comment'
															? 'purple'
															: 'green'
												}
											>
												{item.type === 'issue_comment'
													? 'PR Comment'
													: item.type === 'review_comment'
														? 'Review Comment'
														: 'Review'}
											</Tag>
											<a href={item.url} target="_blank" rel="noopener noreferrer">
												View on GitHub →
											</a>
											<Text type="secondary" style={{ fontSize: '12px' }}>
												{dayjs(item.createdAt).fromNow()}
											</Text>
										</Space>
									</Space>
								</List.Item>
							)}
						/>
					</Card>
				)}

				{showSection.reviews && details?.reviews && (
					<Card title="All Reviews">
						<List
							size="small"
							dataSource={details.reviews}
							renderItem={(item: PRDetails) => (
								<List.Item>
									<Space
										direction="vertical"
										size={2}
										style={{ width: '100%', wordBreak: 'break-word' }}
									>
										<Text>{item.body || item.title}</Text>
										<Space>
											<Tag color="purple">Review</Tag>
											<a href={item.url} target="_blank" rel="noopener noreferrer">
												View on GitHub →
											</a>
											<Text type="secondary" style={{ fontSize: '12px' }}>
												{dayjs(item.createdAt).fromNow()}
											</Text>
										</Space>
									</Space>
								</List.Item>
							)}
						/>
					</Card>
				)}

				{showSection.prs && (
					<>
						{openPRs.length > 0 && (
							<Card
								title={
									<Text strong style={{ color: '#f5222d' }}>
										Open Pull Requests ({openPRs.length})
									</Text>
								}
							>
								<List
									size="small"
									dataSource={openPRs}
									renderItem={(item: PRDetails) => (
										<List.Item>
											<Space
												direction="vertical"
												size={2}
												style={{ width: '100%', wordBreak: 'break-word' }}
											>
												<Space>
													<Tag color="red">Open</Tag>
													<Text strong>{item.title}</Text>
												</Space>
												<Space>
													<Text type="secondary" style={{ fontSize: '12px' }}>
														Opened {dayjs(item.createdAt).fromNow()}
													</Text>
													<Text type="secondary">•</Text>
													<Text type="secondary" style={{ fontSize: '12px' }}>
														{item.url.includes('dashboard-frontend')
															? 'dashboard-frontend'
															: item.url.includes('dashboard-backend')
																? 'dashboard-backend'
																: 'Receptionist'}
													</Text>
													<Text type="secondary">•</Text>
													<a href={item.url} target="_blank" rel="noopener noreferrer">
														View on GitHub →
													</a>
												</Space>
											</Space>
										</List.Item>
									)}
								/>
							</Card>
						)}

						{closedPRs.length > 0 && (
							<Card
								title={
									<Text strong style={{ color: '#52c41a' }}>
										Closed Pull Requests ({closedPRs.length})
									</Text>
								}
							>
								<List
									size="small"
									dataSource={closedPRs}
									renderItem={(item: PRDetails) => (
										<List.Item>
											<Space
												direction="vertical"
												size={2}
												style={{ width: '100%', wordBreak: 'break-word' }}
											>
												<Space>
													<Tag color="green">Closed</Tag>
													<Text strong>{item.title}</Text>
												</Space>
												<Space>
													<Text type="secondary" style={{ fontSize: '12px' }}>
														Closed {dayjs(item.createdAt).fromNow()}
													</Text>
													<Text type="secondary">•</Text>
													<Text type="secondary" style={{ fontSize: '12px' }}>
														{item.url.includes('dashboard-frontend')
															? 'dashboard-frontend'
															: item.url.includes('dashboard-backend')
																? 'dashboard-backend'
																: 'Receptionist'}
													</Text>
													<Text type="secondary">•</Text>
													<a href={item.url} target="_blank" rel="noopener noreferrer">
														View on GitHub →
													</a>
												</Space>
											</Space>
										</List.Item>
									)}
								/>
							</Card>
						)}
					</>
				)}

				<Card>
					<Space direction="vertical">
						<Title level={5}>View All on GitHub:</Title>
						<Space direction="vertical" style={{ width: '100%' }}>
							<a
								href={`https://github.com/Ai-Front-Desk/dashboard-frontend/pulls?q=author:${author}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Space>
									<GithubOutlined />
									dashboard-frontend PRs
								</Space>
							</a>
							<a
								href={`https://github.com/Ai-Front-Desk/dashboard-backend/pulls?q=author:${author}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Space>
									<GithubOutlined />
									dashboard-backend PRs
								</Space>
							</a>
							<a
								href={`https://github.com/Ai-Front-Desk/Receptionist/pulls?q=author:${author}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Space>
									<GithubOutlined />
									Receptionist PRs
								</Space>
							</a>
						</Space>
					</Space>
				</Card>
			</Space>
		</Modal>
	);
};

const MetricTooltip: React.FC<{
	value: number;
	metric: string;
	rank: number;
	total: number;
	goodDirection: 'high' | 'low';
	author: string;
	data: PRStatsWithKey[];
	dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}> = ({ value, metric, rank, total, goodDirection, author, data, dateRange }) => {
	const [modalVisible, setModalVisible] = useState(false);
	const percentile = ((rank / total) * 100).toFixed(0);
	const isGood = goodDirection === 'high' ? rank <= total * 0.3 : rank >= total * 0.7;
	const isBad = goodDirection === 'high' ? rank >= total * 0.7 : rank <= total * 0.3;

	let message = `Click to see details and links\n${percentile}th percentile for ${metric}`;
	if (isGood) {
		message += '\n🌟 Keep up the great work!';
	} else if (isBad) {
		message += "\n💪 There's room for improvement here.";
	}

	return (
		<>
			<Tooltip title={message}>
				<Button
					type="link"
					style={{
						padding: 0,
						color: isGood ? '#389e0d' : isBad ? '#cf1322' : undefined,
						fontWeight: isGood ? 'bold' : undefined,
						textDecoration: 'underline',
						cursor: 'pointer',
					}}
					onClick={() => setModalVisible(true)}
				>
					{value.toFixed(1)}
				</Button>
			</Tooltip>
			<MetricModal
				visible={modalVisible}
				onClose={() => setModalVisible(false)}
				title={`${metric.charAt(0).toUpperCase() + metric.slice(1)} Details`}
				author={author}
				metric={metric}
				value={value}
				rank={rank}
				total={total}
				data={data}
				dateRange={dateRange}
			/>
		</>
	);
};

const InsightCard: React.FC<{
	title: string;
	good?: PRStatsWithKey[];
	bad?: PRStatsWithKey[];
	metric: string;
	icon: React.ReactNode;
	color: string;
}> = ({ title, good, bad, metric, icon, color }) => (
	<Card
		size="small"
		title={
			<Space>
				{React.cloneElement(icon as React.ReactElement, { style: { color } })}
				<Text strong>{title}</Text>
			</Space>
		}
		style={{
			height: '100%',
			boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
			borderRadius: '8px',
		}}
		bodyStyle={{ padding: '16px' }}
	>
		{good && (
			<>
				<Paragraph strong style={{ color: '#389e0d', marginBottom: 12, fontSize: '15px' }}>
					🏆 Top Performers
				</Paragraph>
				<List
					size="small"
					dataSource={good}
					renderItem={(user) => (
						<List.Item>
							<Space>
								<Text strong>{user.author}</Text>
								<Text type="secondary">
									{(user[metric as keyof PRStatsWithKey] as number).toFixed(1)}
									{metric === 'avgDaysToClose' ? ' days' : ''}
								</Text>
							</Space>
						</List.Item>
					)}
				/>
			</>
		)}
		{bad && (
			<>
				<Paragraph
					strong
					style={{ color: '#cf1322', marginTop: 16, marginBottom: 12, fontSize: '15px' }}
				>
					🐌 Bottom Performers
				</Paragraph>
				<List
					size="small"
					dataSource={bad}
					renderItem={(user) => (
						<List.Item>
							<Space>
								<Text strong>{user.author}</Text>
								<Text type="secondary">
									{(user[metric as keyof PRStatsWithKey] as number).toFixed(1)}
									{metric === 'avgDaysToClose' ? ' days' : ''}
								</Text>
							</Space>
						</List.Item>
					)}
				/>
			</>
		)}
	</Card>
);

const getLetterGrade = (rank: number, total: number): string => {
	const percentile = ((total - rank + 1) / total) * 100;

	if (percentile >= 95) {
		return 'A+';
	}
	if (percentile >= 90) {
		return 'A';
	}
	if (percentile >= 85) {
		return 'A-';
	}
	if (percentile >= 75) {
		return 'B+';
	}
	if (percentile >= 65) {
		return 'B';
	}
	if (percentile >= 55) {
		return 'B-';
	}
	if (percentile >= 45) {
		return 'C+';
	}
	if (percentile >= 35) {
		return 'C';
	}
	if (percentile >= 25) {
		return 'C-';
	}
	if (percentile >= 17.5) {
		return 'D+';
	}
	if (percentile >= 10) {
		return 'D';
	}
	return 'F';
};

const getGradeColor = (grade: string): string => {
	if (grade.startsWith('A')) {
		return '#389e0d';
	} // Green
	if (grade.startsWith('B')) {
		return '#096dd9';
	} // Blue
	if (grade.startsWith('C')) {
		return '#d48806';
	} // Gold
	if (grade.startsWith('D')) {
		return '#d4b106';
	} // Yellow
	return '#cf1322'; // Red for F
};

const getTimeColor = (days: number) => {
	if (days <= 2) {
		return '#52c41a';
	} // green
	if (days <= 4) {
		return '#faad14';
	} // yellow
	return '#f5222d'; // red
};

// const getOpenPRColor = (count: number) => {
//   if (count === 0) return '#52c41a'; // green - ideal
//   if (count <= 3) return '#1890ff'; // blue - sweet spot
//   if (count <= 5) return '#faad14'; // yellow - getting high
//   return '#f5222d'; // red - too many
// };

export const EnhancedPRStatsTable: React.FC<EnhancedPRStatsTableProps> = ({
	data,
	loading,
	dateRange,
}) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedScore, setSelectedScore] = useState<'engineeringScore' | 'seniorEngineeringScore'>(
		'engineeringScore',
	);

	const getTopPerformers = (field: keyof PRStatsWithKey, count = 3) => {
		const sorted = [...data].sort((a, b) => (b[field] as number) - (a[field] as number));
		return sorted.slice(0, count);
	};

	const getBottomPerformers = (field: keyof PRStatsWithKey, count = 3) => {
		const sorted = [...data].sort((a, b) => (a[field] as number) - (b[field] as number));
		return sorted.slice(0, count);
	};

	const getRank = (value: number, field: keyof PRStatsWithKey, ascending = true) => {
		const sorted = [...data].sort((a, b) =>
			ascending
				? (a[field] as number) - (b[field] as number)
				: (b[field] as number) - (a[field] as number),
		);
		return sorted.findIndex((item) => (item[field] as number) === value) + 1;
	};

	const metrics = useMemo(() => {
		const performers = {
			topPRs: new Set(getTopPerformers('totalPRs').map((x) => x.key)),
			fastClosers: new Set(getBottomPerformers('avgDaysToClose').map((x) => x.key)),
			topCommenters: new Set(getTopPerformers('commentsOnOthersPRs').map((x) => x.key)),
			topReviewers: new Set(getTopPerformers('prReviews').map((x) => x.key)),
			topEngineers: new Set(getTopPerformers('engineeringScore').map((x) => x.key)),
			topSeniors: new Set(getTopPerformers('seniorEngineeringScore').map((x) => x.key)),
			slowClosers: new Set(getTopPerformers('avgDaysToClose').map((x) => x.key)),
			reviewDodgers: new Set(getBottomPerformers('prReviews').map((x) => x.key)),
		};
		return performers;
	}, [data]);

	const renderScoreCell = (
		value: number,
		record: PRStatsWithKey,
		scoreType: 'engineeringScore' | 'seniorEngineeringScore',
	) => {
		const isTopPerformer = metrics[
			scoreType === 'engineeringScore' ? 'topEngineers' : 'topSeniors'
		].has(record.key);
		const rank = getRank(value, scoreType, false);
		const grade = getLetterGrade(rank, data.length);
		const color = getGradeColor(grade);
		const percentile = (((data.length - rank + 1) / data.length) * 100).toFixed(1);

		return (
			<Space>
				<Text strong={isTopPerformer}>
					<span style={{ color }}>{grade}</span>
					<span style={{ marginLeft: 8, color: '#8c8c8c' }}>({value})</span>
					<span style={{ marginLeft: 4, fontSize: '12px', color: '#8c8c8c' }}>{percentile}%</span>
				</Text>
				{isTopPerformer && <CrownOutlined style={{ color: '#ffd700' }} />}
				<Button
					type="link"
					icon={<InfoCircleOutlined />}
					onClick={() => {
						setSelectedScore(scoreType);
						setModalVisible(true);
					}}
				/>
			</Space>
		);
	};

	// const renderMetricBadges = (record: PRStatsWithKey) => {
	//   const badges = [];
	//   if (metrics.topPRs.has(record.key))
	//     badges.push(
	//       <Tag color='blue' icon={<RocketOutlined />}>
	//         Top Contributor
	//       </Tag>
	//     );
	//   if (metrics.fastClosers.has(record.key))
	//     badges.push(
	//       <Tag color='green' icon={<ClockCircleOutlined />}>
	//         Fast Closer
	//       </Tag>
	//     );
	//   if (metrics.topReviewers.has(record.key))
	//     badges.push(
	//       <Tag color='purple' icon={<TrophyOutlined />}>
	//         Top Reviewer
	//       </Tag>
	//     );
	//   if (metrics.slowClosers.has(record.key))
	//     badges.push(
	//       <Tag color='red' icon={<ClockCircleOutlined />}>
	//         Needs Speed
	//       </Tag>
	//     );
	//   if (metrics.reviewDodgers.has(record.key)) badges.push(<Tag color='orange'>Review More</Tag>);
	//   return <Space>{badges}</Space>;
	// };

	const columns: ColumnsType<PRStatsWithKey> = [
		{
			title: 'Author',
			dataIndex: 'author',
			key: 'author',
			fixed: 'left',
			width: 200,
			render: (text, record) => (
				<Space>
					<Text strong>{text}</Text>
					<Text type="secondary">({record.slackName})</Text>
				</Space>
			),
			sorter: (a, b) => a.author.localeCompare(b.author),
		},
		{
			title: 'Open PRs',
			dataIndex: 'openPRs',
			key: 'openPRs',
			width: 150,
			render: (value: number, record) => {
				// const color = getOpenPRColor(value);
				// let status = '';
				if (value === 0) {
					status = '✨ Perfect! No open PRs';
				} else if (value <= 3) {
					status = '👍 Good! Within the sweet spot (1-3 PRs)';
				} else if (value <= 5) {
					status = '⚠️ Getting high, try to close some';
				} else {
					status = '🚨 Too many open PRs, focus on closing some';
				}

				return (
					<MetricTooltip
						value={value}
						metric="open PRs"
						rank={getRank(value, 'openPRs', false)}
						total={data.length}
						goodDirection="low"
						author={record.author}
						data={data}
						dateRange={dateRange}
					/>
				);
			},
			sorter: (a, b) => (a.openPRs || 0) - (b.openPRs || 0),
		},
		{
			title: dateRange ? 'PRs Closed (In Range)' : 'Total PRs',
			dataIndex: dateRange ? 'prsClosedInRange' : 'totalPRs',
			key: dateRange ? 'prsClosedInRange' : 'totalPRs',
			width: 150,
			render: (value: number, record) => (
				<MetricTooltip
					value={value}
					metric={dateRange ? 'PRs closed in range' : 'total PRs'}
					rank={getRank(value, dateRange ? 'prsClosedInRange' : 'totalPRs', false)}
					total={data.length}
					goodDirection="high"
					author={record.author}
					data={data}
					dateRange={dateRange}
				/>
			),
			sorter: (a: PRStatsWithKey, b: PRStatsWithKey) => {
				const field = dateRange ? 'prsClosedInRange' : 'totalPRs';
				return (a[field] || 0) - (b[field] || 0);
			},
		},
		{
			title: 'Avg Days to Close',
			dataIndex: 'avgDaysToClose',
			key: 'avgDaysToClose',
			width: 150,
			render: (value: number) => {
				const days = value || 0;
				return <Text style={{ color: getTimeColor(days) }}>{days.toFixed(1)}</Text>;
			},
			sorter: (a, b) => (a.avgDaysToClose || 0) - (b.avgDaysToClose || 0),
		},
		{
			title: 'Comments',
			dataIndex: 'commentsOnOthersPRs',
			key: 'commentsOnOthersPRs',
			sorter: (a, b) => a.commentsOnOthersPRs - b.commentsOnOthersPRs,
			render: (value, record) => (
				<MetricTooltip
					value={value}
					metric="comments on others PRs"
					rank={getRank(value, 'commentsOnOthersPRs', false)}
					total={data.length}
					goodDirection="high"
					author={record.author}
					data={data}
					dateRange={dateRange}
				/>
			),
		},
		{
			title: 'Reviews',
			dataIndex: 'prReviews',
			key: 'prReviews',
			sorter: (a, b) => a.prReviews - b.prReviews,
			render: (value, record) => (
				<MetricTooltip
					value={value}
					metric="PR reviews"
					rank={getRank(value, 'prReviews', false)}
					total={data.length}
					goodDirection="high"
					author={record.author}
					data={data}
					dateRange={dateRange}
				/>
			),
		},
		{
			title: 'Engineering Score',
			dataIndex: 'engineeringScore',
			key: 'engineeringScore',
			sorter: (a, b) => a.engineeringScore - b.engineeringScore,
			render: (value, record) => renderScoreCell(value, record, 'engineeringScore'),
		},
		{
			title: 'Senior Score',
			dataIndex: 'seniorEngineeringScore',
			key: 'seniorEngineeringScore',
			sorter: (a, b) => a.seniorEngineeringScore - b.seniorEngineeringScore,
			render: (value, record) => renderScoreCell(value, record, 'seniorEngineeringScore'),
		},
	];

	const teamStats = useMemo(() => {
		const total = data.reduce(
			(acc, curr) => ({
				prs: acc.prs + curr.totalPRs,
				reviews: acc.reviews + curr.prReviews,
				comments: acc.comments + curr.commentsOnOthersPRs,
			}),
			{ prs: 0, reviews: 0, comments: 0 },
		);

		return (
			<Card
				style={{
					marginBottom: 16,
					background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
					borderRadius: '8px',
					boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
				}}
			>
				<Space size="large">
					<Statistic
						title={<Text strong>Total PRs</Text>}
						value={total.prs}
						prefix={<RocketOutlined style={{ color: '#1890ff' }} />}
					/>
					<Statistic
						title={<Text strong>Total Reviews</Text>}
						value={total.reviews}
						prefix={<TrophyOutlined style={{ color: '#722ed1' }} />}
					/>
					<Statistic
						title={<Text strong>Total Comments</Text>}
						value={total.comments}
						prefix={<MessageOutlined style={{ color: '#52c41a' }} />}
					/>
				</Space>
			</Card>
		);
	}, [data]);

	const insights = (
		<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
			<Col span={12}>
				<InsightCard
					title="Code Review Champions"
					good={getTopPerformers('prReviews', 3)}
					bad={getBottomPerformers('prReviews', 3)}
					metric="prReviews"
					icon={<TrophyOutlined />}
					color="#722ed1"
				/>
			</Col>
			<Col span={12}>
				<InsightCard
					title="PR Velocity"
					good={getBottomPerformers('avgDaysToClose', 3)}
					bad={getTopPerformers('avgDaysToClose', 3)}
					metric="avgDaysToClose"
					icon={<ThunderboltOutlined />}
					color="#1890ff"
				/>
			</Col>
		</Row>
	);

	const dateRangeText = dateRange
		? `Showing data from ${dateRange[0].format('MMM D, YYYY')} to ${dateRange[1].format('MMM D, YYYY')}`
		: 'Showing all time data';

	return (
		<>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Title level={4}>{dateRangeText}</Title>
				{insights}
				{teamStats}
				<Table
					columns={columns}
					dataSource={data}
					loading={loading}
					pagination={false}
					scroll={{ x: 1500 }}
					sticky
				/>
				<ScoreExplanationModal
					visible={modalVisible}
					onClose={() => setModalVisible(false)}
					explanation={SCORE_EXPLANATIONS[selectedScore]}
					title={
						selectedScore === 'engineeringScore'
							? 'Engineering Score Explanation'
							: 'Senior Engineering Score Explanation'
					}
				/>
			</Space>
			<style>{`
        .highlight-row {
          background-color: rgba(24, 144, 255, 0.05);
        }
        .highlight-row:hover {
          background-color: rgba(24, 144, 255, 0.1) !important;
        }
      `}</style>
		</>
	);
};

export default EnhancedPRStatsTable;
